/**
 * Clipboard utility functions
 */

import { useState } from 'react';

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const result = document.execCommand('copy');
      document.body.removeChild(textArea);
      return result;
    }
  } catch {
    return false;
  }
}

export type CopyState = 'idle' | 'copying' | 'copied' | 'error';

export function useClipboardCopy(resetDelay: number = 1500) {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  
  const copy = async (text: string): Promise<boolean> => {
    setCopyState('copying');
    
    try {
      const success = await copyToClipboard(text);
      setCopyState(success ? 'copied' : 'error');
      
      if (success) {
        setTimeout(() => setCopyState('idle'), resetDelay);
      } else {
        setTimeout(() => setCopyState('idle'), resetDelay);
      }
      
      return success;
    } catch {
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), resetDelay);
      return false;
    }
  };
  
  return { copy, copyState };
}
